import React from "react";
import { useTranslation } from "react-i18next";
import ChevronIcon from "../../../assets/icons/grey-bottom-chevron.svg";
import PlusPurpleIcon from "../../../assets/icons/plus-purple.svg";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectScriptCategoriesByCreator } from "../../../redux/chatScript/selectors/scriptSelectors";
import { createScriptCategoryAction, fetchScriptCategoriesByCreator } from "../../../redux/chatScript/thunks/scriptThunks";
import "./AddOrCreateScriptCategory.less";

type Props = {
  creatorId: string;
  selectedCategoryId?: string;
  onSelect: (id: string) => void;
  selectedCategoryName?: string;
};

const AddOrCreateScriptCategory: React.FC<Props> = ({ creatorId, selectedCategoryId, onSelect, selectedCategoryName }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const categories = useAppSelector(selectScriptCategoriesByCreator(creatorId));
  const [open, setOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState("");

  React.useEffect(() => {
    if (creatorId && (!categories || categories.length === 0)) {
      dispatch(fetchScriptCategoriesByCreator(creatorId));
    }
  }, [creatorId, categories?.length, dispatch]);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const toggle = () => setOpen((o) => !o);
  const handleSelect = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  const selectedLabel = React.useMemo(() => {
    if (!selectedCategoryId) return selectedCategoryName;
    const found = categories?.find((c) => c._id === selectedCategoryId)?.name;
    return found || selectedCategoryName;
  }, [categories, selectedCategoryId, selectedCategoryName]);

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
        setNewName("");
      }
    };
    if (open) document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  return (
    <div ref={containerRef} className="add-or-create-script-category" role="button" aria-haspopup="listbox" aria-expanded={open}>
      <div className="aocc-trigger" onClick={toggle}>
        <div className="aocc-left">
          <div className="aocc-label">{t("bookmark")}</div>
          {creating ? (
            <input
              className="aocc-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t("edit-or-create-category-input-placeholder") as string}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <div className="aocc-title">{selectedLabel || t("select-or-create-bookmark")}</div>
          )}
        </div>
        {!creating && <img src={ChevronIcon} alt="chevron" className={`aocc-chevron ${open ? "open" : ""}`} />}
      </div>

      {open && !creating && (
        <div className="aocc-popup" role="listbox" aria-label="script-categories" onClick={(e) => e.stopPropagation()}>
          <div className="aocc-header">
            <button
              type="button"
              className="aocc-new-btn"
              aria-label="new-bookmark"
              title={t("new-bookmark") as string}
              onClick={(e) => {
                e.stopPropagation();
                setCreating(true);
                setOpen(false);
              }}
            >
              <img src={PlusPurpleIcon} alt="plus" />
              <span>{t("new-bookmark")}</span>
            </button>
          </div>
          {categories && categories.length > 0 ? (
            <ul className="aocc-list">
              {categories.map((c) => (
                <li
                  key={c._id}
                  className={`aocc-item ${selectedCategoryId === c._id ? "selected" : ""}`}
                  role="option"
                  aria-selected={selectedCategoryId === c._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(c._id);
                  }}
                >
                  {c.name}
                </li>
              ))}
            </ul>
          ) : (
            <div className="aocc-empty">{t("no-bookmark-created")}</div>
          )}
        </div>
      )}

      {creating && (
        <div className="aocc-create" onClick={(e) => e.stopPropagation()}>
          <div className="aocc-create-panel">
            <button
              type="button"
              className="aocc-btn aocc-btn--primary"
              disabled={!newName.trim()}
              onClick={async () => {
                const name = newName.trim();
                if (!name) return;
                try {
                  const res = await dispatch(createScriptCategoryAction({ name, creatorId })).unwrap();
                  onSelect(res.category._id);
                } finally {
                  setCreating(false);
                  setOpen(false);
                  setNewName("");
                }
              }}
            >
              {t("create")}
            </button>
            <button
              type="button"
              className="aocc-btn aocc-btn--secondary"
              onClick={() => {
                setCreating(false);
                setNewName("");
              }}
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOrCreateScriptCategory;